import { useState, useCallback, useEffect } from 'react';

/**
 * Custom hook for managing Dev Autobattler state and actions
 */
export const useDevAutobattler = (ws, userId, initialGameState = null, initialGameId = null, initialTurn = 1) => {
  const [gameState, setGameState] = useState(initialGameState);
  const [gameId, setGameId] = useState(initialGameId);
  const [currentTurn, setCurrentTurn] = useState(initialTurn);
  const [turnIndex, setTurnIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [logFilePath, setLogFilePath] = useState(null);

  /**
   * Start dev autobattler game
   */
  const startDevAutobattler = useCallback(() => {
    if (!ws || !userId) {
      setError('WebSocket or user ID not available');
      return;
    }

    // ✅ CRITICAL: Check WebSocket readyState before sending
    if (ws.readyState !== WebSocket.OPEN) {
      setError('WebSocket is not ready. Please wait for connection.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      ws.send(JSON.stringify({
        type: 'dev-autobattler-start'
      }));
    } catch (error) {
      setError(`Failed to send message: ${error.message}`);
      setIsLoading(false);
    }
  }, [ws, userId]);

  /**
   * Advance to next turn
   */
  const nextTurn = useCallback(() => {
    if (!ws || !gameId) {
      setError('WebSocket or game ID not available');
      return;
    }

    // ✅ CRITICAL: Check WebSocket readyState before sending
    if (ws.readyState !== WebSocket.OPEN) {
      setError('WebSocket is not ready. Please wait for connection.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      ws.send(JSON.stringify({
        type: 'dev-autobattler-next-turn',
        gameId
      }));
    } catch (error) {
      setError(`Failed to send message: ${error.message}`);
      setIsLoading(false);
    }
  }, [ws, gameId]);

  /**
   * Reload config
   */
  const reloadConfig = useCallback(() => {
    if (!ws || !gameId) {
      setError('WebSocket or game ID not available');
      return;
    }

    // ✅ CRITICAL: Check WebSocket readyState before sending
    if (ws.readyState !== WebSocket.OPEN) {
      setError('WebSocket is not ready. Please wait for connection.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      ws.send(JSON.stringify({
        type: 'dev-autobattler-reload-config',
        gameId
      }));
    } catch (error) {
      setError(`Failed to send message: ${error.message}`);
      setIsLoading(false);
    }
  }, [ws, gameId]);

  /**
   * Request current state
   */
  const requestState = useCallback(() => {
    if (!ws || !gameId) {
      return;
    }

    // ✅ CRITICAL: Check WebSocket readyState before sending
    if (ws.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket is not ready, cannot request state');
      return;
    }

    try {
      ws.send(JSON.stringify({
        type: 'dev-autobattler-state',
        gameId
      }));
    } catch (error) {
      console.error(`Failed to send state request: ${error.message}`);
    }
  }, [ws, gameId]);

  /**
   * Handle WebSocket messages
   */
  useEffect(() => {
    if (!ws) return;

    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case 'dev-autobattler-started': {
            setGameState(data.gameState);
            setGameId(data.gameId);
            setCurrentTurn(data.currentTurn || 1);
            setTurnIndex(0);
            setLogFilePath(data.logFilePath || null);
            setIsLoading(false);
            setError(null);
            break;
          }

          case 'dev-autobattler-turn-complete': {
            // ✅ CRITICAL: Do NOT update gameState here - BattleUI handles it through WebSocket
            // Only update turn tracking info
            setCurrentTurn(data.currentTurn || 1);
            setTurnIndex(data.turnIndex || 0);
            setLogFilePath(data.logFilePath || null);
            setIsLoading(false);
            setError(null);
            break;
          }

          case 'dev-autobattler-game-ended': {
            setGameState(data.gameState);
            setCurrentTurn(data.currentTurn || 1);
            setTurnIndex(data.turnIndex || 0);
            setLogFilePath(data.logFilePath || null);
            setIsLoading(false);
            setError(null);
            break;
          }

          case 'dev-autobattler-config-reloaded': {
            setIsLoading(false);
            setError(null);
            // Optionally show a success message
            break;
          }

          case 'dev-autobattler-state': {
            setGameState(data.gameState);
            setCurrentTurn(data.currentTurn || 1);
            setTurnIndex(data.turnIndex || 0);
            setLogFilePath(data.logFilePath || null);
            setIsLoading(false);
            setError(null);
            break;
          }

          case 'error': {
            setIsLoading(false);
            setError(data.message || 'An error occurred');
            break;
          }

          default:
            // Ignore other message types
            break;
        }
      } catch (err) {
        console.error('Error parsing dev autobattler message:', err);
        setError('Failed to parse server message');
      }
    };

    ws.addEventListener('message', handleMessage);

    return () => {
      ws.removeEventListener('message', handleMessage);
    };
  }, [ws]);

  // Check if can advance turn
  // Can advance if game is not ended and not loading
  const canAdvanceTurn = !isLoading && gameState && !gameState.gameEnded && !gameState.winner;

  return {
    gameState,
    gameId,
    currentTurn,
    turnIndex,
    isLoading,
    error,
    logFilePath,
    canAdvanceTurn,
    startDevAutobattler,
    nextTurn,
    reloadConfig,
    requestState
  };
};
